import InfoIcon from "@mui/icons-material/Info";
import { Box, Stack, Button, Typography } from "@mui/material";
import { useRecordContext, toggleListItemExpand, useDeleteWithConfirmController, useListContext } from "ra-core";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Confirm from "@/components/widgets/Confirm";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import WithSave from "@/controller/WithSave";
import { DataClassificationRuleDto, DataClassificationRuleInput } from "@/gen/api/encrypt";
import DataClassificationRuleEdit from "./DataClassificationRuleEdit";
import { LocalMessageLocation, useLocalMessagingRoot } from "@/contexts/LocalMessagingRoot";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { useLmGlobalDisabledMsg } from "@/contexts/LocalMessageHookProtocols/useLmGlobalDisabledMsg";



const clssRulesRes = 'settings/encryption/classification-rules'

const RowExpandDataDataClassificationRuleEdit: FC = () => {
  const ctxRecord : DataClassificationRuleDto = useRecordContext() as any
  // const { changePendingIds, allRules, refetch } = ctrls

  return (
    <RowExpandDataDataClassificationRuleEditBody currentId={ctxRecord.ruleId!} />
  )
}

const RowExpandDataDataClassificationRuleEditBody: FC<{
  currentId: number;
}> = ({ currentId }) => {
  const record = useRecordContext()
  const dto: DataClassificationRuleDto = record as any
  const dispatch = useDispatch()
  const { refetch } = useListContext()

  const [states, setStates] = useState({
    dirty: false,
  })
  
  const {LocalMessagingRootHost, subscribe, publish} = useLocalMessagingRoot()
  const {assembleSubStates} = useLmStatesPropagate({ 
    key: 'root',
    asUpperStage: {
      dirtyStateInformed: {
        handler() {
          return (_, dirty) => setStates(v => ({...v, dirty }))
        },
        deps: []
      }
    }
  }, {subscribe, publish})
  useLmGlobalDisabledMsg({ authority }, {subscribe, publish})
  
  const closeExpanded = () => {
    dispatch(toggleListItemExpand(clssRulesRes, currentId))
  }
  
  const dialogRef = useRef<HTMLElement|null>(null)
  useEffect(() => {
    setTimeout(() => {
      dialogRef.current?.focus()
    })
  }, [])

  
  const handlers = useDefaultSavedHandlers("Update Classification Rule")
  const revertHandlers = useDefaultSavedHandlers("Revert Classification Rule")
  refetch && revertHandlers.onSuccess.merge(refetch)

  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    // if (!hasClssPermit && !hasEditPermit) { return }
    if (!states.dirty) { return }

    try {
      const payload: DataClassificationRuleInput = (await assembleSubStates(['editor'])).editor
      payload.ruleId = dto.ruleId
      
      save(payload, {
        onSuccess: () => {
          refetch && refetch()
          closeExpanded()
        }
      })
    } catch (error) {
      // validation must had failed
    }
  }

  const delHandlers = useDefaultSavedHandlers('Delete Data Classification Rule')
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch && refetch()
  }, [refetch]))

  const {
    open: delConfirmOpen,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
    loading: delSaving,
  } = useDeleteWithConfirmController({
    resource: clssRulesRes,
    record,
    mutationMode: 'pessimistic',
    ...delHandlers,
  });

  const setRuleToBeDeleted = useCallback((r:DataClassificationRuleDto) => {
    handleDialogOpen({stopPropagation:()=>{}} as any)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box role="dialog">
      <span tabIndex={-1} ref={dialogRef} className="sr-only">Field Edit Region for {`${dto.displayName}`}</span>
      <Box sx={{ mb: 2, px: 4, }}>
        <LocalMessagingRootHost>
          <LocalMessageLocation id="editor">
            <DataClassificationRuleEdit />
          </LocalMessageLocation>
        </LocalMessagingRootHost>
      </Box>
      <Stack direction="row" sx={{
        mb: -2, mx: -2, py: '10px', px: 5,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        '& > button': { width: 96, mr: 2, },
      }}>
        {/* {(hasClssPermit || hasEditPermit) &&  */}
        {true && 
          <CreateOrEdit record={record} id={record.id} resource={clssRulesRes} basePath="" {...handlers}>
            <WithSave 
              render={({save, saving}) =>
                <Button
                  disabled={!states.dirty || saving}
                  variant="contained" 
                  size="small" 
                  color="primary"
                  onClick={handleSave(save)}
                >
                  Save
                </Button>
              }
            />
          </CreateOrEdit>}
        <Button
          variant="outlined" 
          size="small" 
          color="primary"
          onClick={closeExpanded}
        >
          Cancel
        </Button>
        <Box sx={{ mx: 'auto' }} />
        <Button variant="contained" size="small" color="error"
          onClick={() => setRuleToBeDeleted(dto)}
        >
          Delete
        </Button>
      </Stack>
      <Confirm
        isOpen={delConfirmOpen}
        loading={delSaving}
        title={"Delete Data Classification Rule"}
        onConfirm={handleDelete}
        onClose={(e:any) => {
          handleDialogClose(e)
        }}
        content={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Please confirm whether to proceed with the deletion of this
              {` "${record?.displayName}" `}
              Data Classification Rule.
            </Typography>
          </Box>
        }
      />
    </Box>
  )
}

export default RowExpandDataDataClassificationRuleEdit;

const authority = () => Promise.resolve(false)
